import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { UserResponse } from '@troia-v3/types';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { StatusPill, Switch } from '@/shared/components/ui';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { Icon } from '@/shared/icons';
import { formatDocumentInput } from '@/shared/utils/documentMask';

/**
 * Linha de tabela de usuário (view "Lista"). Padrão-lista travado:
 * checkbox de seleção (1ª col) + colunas multi-linha (nome+email) + menu ••• (última col).
 */
export interface UserListItemProps {
  user: UserResponse;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
  onDelete?: (id: string) => void;
  onToggleStatus?: (id: string, currentStatus: string) => void;
  onAvatarChange?: (id: string, file: File) => void;
  /** Abre o mini-modal de troca de nível para este usuário */
  onChangeLevel?: (id: string) => void;
  /** Reenvia o convite (e-mail de verificação) para este usuário */
  onResendInvite?: (id: string) => void;
  /** Dispara a redefinição de senha deste usuário */
  onResetPassword?: (id: string) => void;
}

export const UserListItem: React.FC<UserListItemProps> = ({
  user,
  selected = false,
  onToggleSelect,
  onDelete,
  onToggleStatus,
  onAvatarChange,
  onChangeLevel,
  onResendInvite,
  onResetPassword,
}) => {
  const navigate = useNavigate();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Último acesso em tempo relativo (mockup: "há 3 dias", não data absoluta)
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Nunca';
    const diff = Date.now() - new Date(dateString).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'agora';
    if (min < 60) return `há ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 24) return `há ${h}h`;
    const d = Math.floor(h / 24);
    if (d < 30) return `há ${d} ${d === 1 ? 'dia' : 'dias'}`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `há ${mo} ${mo === 1 ? 'mês' : 'meses'}`;
    const y = Math.floor(mo / 12);
    return `há ${y} ${y === 1 ? 'ano' : 'anos'}`;
  };

  const getUserInitials = () =>
    `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();

  const handleAvatarClick = () => {
    if (onAvatarChange) fileInputRef.current?.click();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione apenas arquivos de imagem');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Arquivo muito grande. Máximo é 5MB.');
      return;
    }
    onAvatarChange?.(user.id, file);
    e.target.value = '';
  };

  const isActive = user.status === 'active';

  const actions: CardAction[] = [
    { label: 'Editar', icon: 'edit', onClick: () => navigate(`/settings/users/${user.id}/edit`) },
  ];
  if (onChangeLevel) actions.push({ label: 'Alterar nível', icon: 'shield', onClick: () => onChangeLevel(user.id) });
  if (onResendInvite) actions.push({ label: 'Reenviar convite', icon: 'mail', onClick: () => onResendInvite(user.id) });
  if (onResetPassword) actions.push({ label: 'Redefinir senha', icon: 'key', onClick: () => onResetPassword(user.id) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(user.id, user.status),
    });
  }
  if (onDelete) {
    actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(user.id), variant: 'danger', dividerBefore: true });
  }

  return (
    <tr
      className="border-b transition-colors"
      style={{ borderColor: 'var(--theme-card-border)', backgroundColor: selected ? 'var(--theme-primary-background)' : undefined }}
      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
      data-testid={`user-row-${user.id}`}
    >
      {/* Checkbox */}
      <td className="pl-4 pr-1 py-3 w-11">
        <button
          type="button"
          onClick={() => onToggleSelect?.(user.id)}
          className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
          style={{
            backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
            borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
            color: '#ffffff',
          }}
          aria-label={selected ? 'Desmarcar' : 'Selecionar'}
          data-testid={`user-check-${user.id}`}
        >
          {selected && <Icon name="check" className="w-3 h-3" />}
        </button>
      </td>

      {/* Usuário: avatar + nome + email */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
          <div
            className="relative group flex-shrink-0"
            onClick={handleAvatarClick}
            style={{ cursor: onAvatarChange ? 'pointer' : 'default' }}
          >
            <Avatar className="w-9 h-9" isSquircle>
              {user.avatar && <AvatarImage src={user.avatar} alt={`${user.firstName} ${user.lastName}`} isSquircle />}
              <AvatarFallback isSquircle>
                <span className="text-white font-semibold text-xs">{getUserInitials()}</span>
              </AvatarFallback>
            </Avatar>
            {onAvatarChange && (
              <div
                className="absolute inset-0 flex items-center justify-center rounded-[24%] opacity-0 group-hover:opacity-100 transition-opacity"
                style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
              >
                <Icon name="camera" className="w-3 h-3 text-white" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
              {user.firstName} {user.lastName}
            </div>
            <div className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
              {user.email}
            </div>
          </div>
        </div>
      </td>

      {/* Código no sistema externo (ERP) */}
      <td className="px-4 py-3">
        {user.code ? (
          <span className="text-sm font-mono" style={{ color: 'var(--theme-card-text)' }} data-testid={`user-code-${user.id}`}>
            {user.code}
          </span>
        ) : (
          <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>—</span>
        )}
      </td>

      {/* Documento (CPF) — gravado só com dígitos, exibido com máscara */}
      <td className="px-4 py-3">
        {user.document ? (
          <span className="text-sm font-mono whitespace-nowrap" style={{ color: 'var(--theme-card-text)' }} data-testid={`user-document-${user.id}`}>
            {formatDocumentInput(user.document, user.document.length > 11 ? 'PJ' : 'PF')}
          </span>
        ) : (
          <span className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>—</span>
        )}
      </td>

      {/* Nível */}
      <td className="px-4 py-3">
        {user.levelName ? (
          <span
            className="inline-flex items-center gap-1.5 h-[23px] px-2.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
          >
            <Icon name="shield" className="w-3 h-3" />
            {user.levelName}
          </span>
        ) : (
          <span style={{ color: 'var(--theme-muted-text)' }}>—</span>
        )}
      </td>

      {/* Equipes — cap de 2 chips + "+N" (sem cap a linha explode em altura) */}
      <td className="px-4 py-3">
        {user.teams && user.teams.length > 0 ? (
          <div className="flex items-center gap-1.5">
            {user.teams.slice(0, 2).map((t) => (
              <span
                key={t.id}
                className="inline-flex items-center h-[22px] px-2 rounded-md text-xs font-medium max-w-[140px]"
                style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
              >
                <span className="truncate">{t.name}</span>
              </span>
            ))}
            {user.teams.length > 2 && (
              <span
                className="inline-flex items-center h-[22px] px-2 rounded-md text-xs font-semibold"
                style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
                title={user.teams.slice(2).map((t) => t.name).join(', ')}
              >
                +{user.teams.length - 2}
              </span>
            )}
          </div>
        ) : (
          <span style={{ color: 'var(--theme-muted-text)' }}>—</span>
        )}
      </td>

      {/* Status — F47: switch inline (Switch shared sm); pill só quando não togglável (F62) */}
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        {onToggleStatus ? (
          <div className="inline-flex items-center gap-2" data-testid={`user-toggle-${user.id}`}>
            <Switch size="sm" checked={isActive} onChange={() => onToggleStatus(user.id, user.status)} />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativo' : 'Inativo'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativo' : 'Inativo'}
            data-testid={`user-status-${user.id}`}
          />
        )}
      </td>

      {/* Último acesso */}
      <td className="px-4 py-3 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
        {formatDate(user.lastLoginAt)}
      </td>

      {/* Ações */}
      <td className="px-4 py-3 text-right w-14">
        <CardActionsMenu actions={actions} size="sm" data-testid={`user-row-actions-${user.id}`} />
      </td>
    </tr>
  );
};

UserListItem.displayName = 'UserListItem';
